import { useMemo } from 'react';
import { gql, useQuery } from '@apollo/client'

import { useSites } from 'store'
import { TimeRange } from 'models'

import { convertDateRange, getScale } from '../../hooks/use-traffic'
import { useGetStatsTimeRange } from '../../store'
import { filterStatsByTimeRange, generateRanges } from '../use-utils'

const OPERATION_TRAFFIC = gql`
  query TrafficGraphGroupBySsid($sfIds: [ID], $startsAt: String, $endsAt: String) {
    stats: trafficGraphGroupBySsid(sf_ids: $sfIds, starts_at: $startsAt, ends_at: $endsAt) {
      ssid_name
      network_name
      requested_at
      download
      upload
      active_users
    }
  }
`

const generateSsids = ({ stats, ranges }) => {
  const distinctSsids = stats.reduce(
    (acc, { ssid_name, network_name }) =>
      acc.includes([ssid_name, network_name].join('|')) ? acc : [...acc, [ssid_name, network_name].join('|')],
    []
  )
  return ranges.map(range => {
    const statsInRange = filterStatsByTimeRange(stats, range.from, range.to)
    const values = distinctSsids.map(path => {
      const [name, network_name] = path.split('|')
      const totalActiveUsers = statsInRange.reduce((acc, stat) => {
        return stat.ssid_name === name && stat.network_name === network_name ? acc + stat.active_users : acc
      }, 0)
      const totalDownload = statsInRange.reduce((acc, stat) => {
        return stat.ssid_name === name && stat.network_name === network_name ? acc + stat.download : acc
      }, 0)
      const totalUpload = statsInRange.reduce((acc, stat) => {
        return stat.ssid_name === name && stat.network_name === network_name ? acc + stat.upload : acc
      }, 0)
      return {
        name,
        network_name,
        active_users: totalActiveUsers,
        upload: totalUpload,
        download: totalDownload
      }
    })

    return {
      date: range.from,
      ssids: values
    }
  })
}

const useGraph = ({modalTimeRange}) => {
  const [globalRange] = useGetStatsTimeRange()
  const [{ selected }] = useSites()
  const range = modalTimeRange || globalRange || TimeRange.LastTwelveMonths
  const scale = useMemo(() => {
    return getScale(range)
  }, [range])
  const dates = useMemo(() => {
    return convertDateRange(range)
  }, [range])

  const {loading, data} = useQuery(OPERATION_TRAFFIC, {
    variables: {
      sfIds: selected.map(({sf_id}) => sf_id),
      ...dates
    }
  })
  const formattedData = useMemo(() => {
    if (!data) {
      return []
    }
    return data.stats.map(stats => ({
      ...stats,
      download: parseInt(stats.download) / 1000,
      upload: parseInt(stats.upload) / 1000,
      active_users: parseInt(stats.active_users),
    }))
  }, [data])

  const graph = useMemo(() => {
    let ranges = generateRanges({...dates, scale})
    // le dernier jour / mois est supprimé
    if (getScale(range) !== 'year') {
      ranges = ranges.slice(0,-1)
    }
    const graph = generateSsids({stats: formattedData, ranges})
    return graph.map(stats => {
      const download = stats.ssids.reduce((acc, site) => {
        return acc + site.upload + site.download
      }, 0)
      const activeUsers = stats.ssids.reduce((acc, site) => {
        return acc + site.active_users
      }, 0)
      return {
        ...stats,
        average_per_device_ssid: activeUsers ? (download / activeUsers) : 0

      }
    })
  }, [formattedData])
  return {
    loading,
    graph
  }
}

export default useGraph


